import { httpClient } from './http';

const API_BASE_URL = '/subscriptions';

export interface PaymentMethod {
  id: string;
  object: string;
  billing_details: {
    address: {
      city: string | null;
      country: string | null;
      line1: string | null;
      line2: string | null;
      postal_code: string | null;
      state: string | null;
    };
    email: string | null;
    name: string | null;
    phone: string | null;
    tax_id: string | null;
  };
  card: {
    brand: string;
    checks: {
      address_line1_check: string | null;
      address_postal_code_check: string | null;
      cvc_check: string | null;
    };
    country: string;
    display_brand: string;
    exp_month: number;
    exp_year: number;
    fingerprint: string;
    funding: string;
    last4: string;
    networks: {
      available: string[];
      preferred: string | null;
    };
    regulated_status: string;
    three_d_secure_usage: {
      supported: boolean;
    };
    wallet: string | null;
  };
  created: number;
  customer: string;
  livemode: boolean;
  metadata: Record<string, any>;
  radar_options: Record<string, any>;
  type: string;
}

export interface Subscription {
  id: string;
  userId: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  stripePriceId: string;
  status: 'incomplete' | 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  canceledAt?: string;
  trialEnd?: string;
  metadata?: {
    invoice?: {
      id: string;
      status: string;
      created: number;
      pdfUrl: string;
      dueDate: string | null;
      amountDue: number;
      amountPaid: number;
      invoicePdf: string;
      amountRemaining: number;
    };
    priceId: string;
    planName: string;
    subscriptionDetails?: {
      metadata: {
        userId: string;
        planName: string;
        creatorSubscriptionUserId: string;
        billingCycle: string;
      };
      subscription: string;
    };
  };
  features?: Array<string | { description: string; checked?: boolean }>;
  paymentMethods?: PaymentMethod[];
  defaultPaymentMethod?: PaymentMethod;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSubscriptionResponse {
  subscription: Subscription | null;
  client_secret?: string;
  customer_id?: string;
  stripe_subscription_id?: string;
  setup_intent_id?: string;
}

export interface CreateSubscriptionData {
  restaurant_id?: string;
  price_id: string;
  payment_method_id: string;
  coupon_id?: string;
}

export interface CouponInfo {
  id: string;
  name: string;
  percent_off?: number;
  amount_off?: number;
  currency?: string;
  duration: string;
  valid: boolean;
}

class SubscriptionService {
  async getUserSubscription(): Promise<Subscription> {
    try {
      const response = await httpClient.get<any>(`${API_BASE_URL}/user`, {}, 'authenticated');

      if (!response) {
        throw new Error('Error al obtener suscripciones');
      }

      console.log('🔍 SubscriptionService: Suscripción obtenida jajaja:', response.id);
      return response;
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      throw error;
    }
  }

  async getSubscriptionById(id: string): Promise<Subscription> {
    try {
      const response = await httpClient.get<any>(`${API_BASE_URL}/${id}`, {}, 'authenticated');

      if (!response) {
        throw new Error('Error al obtener suscripción');
      }

      return response;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      throw error;
    }
  }

  async createSubscription(subscriptionData: CreateSubscriptionData): Promise<CreateSubscriptionResponse> {
    try {
      const response = await httpClient.post<any>(`${API_BASE_URL}`, subscriptionData, 'authenticated');

      if (!response) {
        throw new Error('Error al crear suscripción');
      }

      return response;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  async cancelSubscription(id: string): Promise<void> {
    try {
      await httpClient.post<any>(`${API_BASE_URL}/${id}/cancel`, {}, 'authenticated');
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  async reactivateSubscription(id: string): Promise<Subscription> {
    try {
      const response = await httpClient.post<any>(`${API_BASE_URL}/${id}/reactivate`, {}, 'authenticated');

      if (!response) {
        throw new Error('Error al reactivar suscripción');
      }

      return response;
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      throw error;
    }
  }

  async createBillingPortalSession(): Promise<{ url: string; customerId: string }> {
    try {
      const response = await httpClient.post<any>(`${API_BASE_URL}/billing-portal`, {}, 'authenticated');

      if (!response || !response.url) {
        throw new Error('Error al crear sesión del portal de facturación');
      }

      return response;
    } catch (error) {
      console.error('Error creating billing portal session:', error);
      throw error;
    }
  }

  async checkActiveSubscriptionByPriceId(priceId: string): Promise<boolean> {
    try {
      const subscription = await this.getUserSubscription();
      return subscription.status === 'active' && subscription.stripePriceId === priceId;
    } catch (error) {
      console.error('Error checking active subscription:', error);
      return false;
    }
  }

  async getPaymentMethods(): Promise<PaymentMethod[]> {
    try {
      console.log('🔍 SubscriptionService: getPaymentMethods ejecutándose...');
      console.log('🔍 SubscriptionService: URL del endpoint:', `${API_BASE_URL}/payment-methods`);
      
      // Verificar headers antes de la llamada
      const headers = await this.getHeadersForDebug();
      console.log('🔍 SubscriptionService: Headers disponibles:', headers);
      
      const response = await httpClient.get<any>(`${API_BASE_URL}/payment-methods`, {}, 'authenticated');
      console.log('📡 SubscriptionService: Respuesta del backend:', response);
      const paymentMethods = response?.paymentMethods || [];
      console.log('💳 SubscriptionService: Métodos de pago procesados:', paymentMethods);
      return paymentMethods;
    } catch (error) {
      console.error('❌ SubscriptionService: Error getting payment methods:', error);
      console.error('❌ SubscriptionService: Error completo:', error);
      return [];
    }
  }

  // Método auxiliar para debug de headers
  private async getHeadersForDebug(): Promise<any> {
    try {
      // Importar headerService dinámicamente para evitar dependencias circulares
      const { headerService } = await import('./headers');
      return {
        hasToken: !!headerService.getAuthToken(),
        tokenLength: headerService.getAuthToken()?.length || 0,
        isAuthenticated: headerService.isAuthenticated()
      };
    } catch (error) {
      return { error: 'No se pudieron obtener headers' };
    }
  }

  async validateCoupon(couponCode: string): Promise<CouponInfo> {
    try {
      const response = await httpClient.post<any>(`${API_BASE_URL}/validate-coupon`, {
        coupon_code: couponCode
      }, 'authenticated');
      return response?.coupon;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error validando cupón');
    }
  }
}

export const subscriptionService = new SubscriptionService(); 